/**
 * QRHealthCheck - Admin tool to audit and repair QR codes for an event
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  QrCode, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw, 
  Search,
  Wrench,
  ChevronDown,
  ChevronUp,
  Download,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';

const QRHealthCheck = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDelegate, setExpandedDelegate] = useState(null);
  const [showOnlyIssues, setShowOnlyIssues] = useState(true);

  // Fetch all delegates for QR audit
  const { data: delegates = [], isLoading } = useQuery({
    queryKey: ['qr-health', eventId],
    queryFn: async () => {
      const response = await api.get(`/events/${eventId}/delegates`);
      // Analyze each delegate's QR status
      return response.data.map(delegate => ({
        ...delegate,
        qrStatus: analyzeQRStatus(delegate)
      }));
    },
    enabled: !!eventId,
    staleTime: 30000
  });

  // Analyze QR status for a delegate
  const analyzeQRStatus = (delegate) => {
    const issues = [];
    const qrData = delegate.qr_data || '';
    const badgeCode = delegate.badge_code || '';
    
    // Check if QR data exists
    if (!qrData) {
      issues.push({ type: 'missing', message: 'No QR data stored' });
    }
    
    // Check QR format
    if (qrData) {
      if (qrData.startsWith('EVT:') && !qrData.startsWith('EVTDLG:')) {
        // New format - validate structure
        const parts = qrData.split(':');
        if (parts.length < 5) {
          issues.push({ type: 'incomplete', message: 'QR data incomplete (missing parts)' });
        }
        if (parts[1] !== delegate.id) {
          issues.push({ type: 'mismatch', message: 'Delegate ID mismatch in QR' });
        }
        if (parts[2] !== eventId) {
          issues.push({ type: 'mismatch', message: 'Event ID mismatch in QR' });
        }
      } else if (qrData.startsWith('EVTDLG:')) {
        // Legacy format - recommend upgrade
        issues.push({ type: 'outdated', message: 'Uses legacy EVTDLG format' });
      } else if (qrData.length === 24) {
        // Just delegate ID
        issues.push({ type: 'outdated', message: 'Uses raw delegate ID format' });
      } else if (!qrData.includes(':')) {
        // Unknown format
        issues.push({ type: 'invalid', message: 'Unknown QR format' });
      }
    }
    
    // Check badge code
    if (!badgeCode) {
      issues.push({ type: 'missing', message: 'No badge code assigned' });
    }
    
    return {
      healthy: issues.length === 0,
      issues,
      format: detectFormat(qrData)
    };
  };

  // Detect QR format
  const detectFormat = (qrData) => {
    if (!qrData) return 'none';
    if (qrData.startsWith('EVT:') && !qrData.startsWith('EVTDLG:')) return 'EVT (current)';
    if (qrData.startsWith('EVTDLG:')) return 'EVTDLG (legacy)';
    if (qrData.length === 24) return 'ObjectId';
    if (qrData.startsWith('DLG-')) return 'Badge Code';
    return 'unknown';
  };

  // Regenerate single QR mutation
  const regenerateMutation = useMutation({
    mutationFn: async (delegateId) => {
      const response = await api.post(`/events/${eventId}/delegates/${delegateId}/regenerate-qr`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`QR regenerated for ${data.delegate_id}`);
      queryClient.invalidateQueries(['qr-health', eventId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to regenerate QR');
    }
  });

  // Batch regenerate mutation
  const batchRegenerateMutation = useMutation({
    mutationFn: async (regenerateAll = false) => {
      const response = await api.post(`/events/${eventId}/batch-regenerate-qr?regenerate_all=${regenerateAll}`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Regenerated ${data.regenerated_count} QR codes`);
      queryClient.invalidateQueries(['qr-health', eventId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Batch regeneration failed');
    }
  });

  // Debug QR query
  const debugQR = async (delegateId) => {
    try {
      const response = await api.get(`/events/${eventId}/delegates/${delegateId}/qr-debug`);
      return response.data;
    } catch (error) {
      toast.error('Failed to fetch QR debug info');
      return null;
    }
  };

  // Filter delegates
  const filteredDelegates = delegates.filter(delegate => {
    const matchesSearch = !searchTerm || 
      delegate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      delegate.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      delegate.badge_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = !showOnlyIssues || !delegate.qrStatus?.healthy;
    
    return matchesSearch && matchesFilter;
  });

  // Calculate stats
  const stats = {
    total: delegates.length,
    healthy: delegates.filter(d => d.qrStatus?.healthy).length,
    issues: delegates.filter(d => !d.qrStatus?.healthy).length,
    legacy: delegates.filter(d => d.qrStatus?.format === 'EVTDLG (legacy)').length,
    missing: delegates.filter(d => !d.qr_data).length
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400 mb-4" />
          <p className="text-slate-500">Analyzing QR codes...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="qr-health-check">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-sm text-slate-500">Total Delegates</div>
        </Card>
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="text-2xl font-bold text-green-600">{stats.healthy}</div>
          <div className="text-sm text-green-600">Healthy QR</div>
        </Card>
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="text-2xl font-bold text-red-600">{stats.issues}</div>
          <div className="text-sm text-red-600">With Issues</div>
        </Card>
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="text-2xl font-bold text-yellow-600">{stats.legacy}</div>
          <div className="text-sm text-yellow-600">Legacy Format</div>
        </Card>
        <Card className="p-4 bg-slate-50 border-slate-200">
          <div className="text-2xl font-bold text-slate-600">{stats.missing}</div>
          <div className="text-sm text-slate-600">Missing QR</div>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            QR Code Repair Tools
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => batchRegenerateMutation.mutate(false)}
              disabled={batchRegenerateMutation.isPending || stats.issues === 0}
              variant="outline"
            >
              {batchRegenerateMutation.isPending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Fix {stats.issues} Issues
            </Button>
            
            <Button
              onClick={() => batchRegenerateMutation.mutate(true)}
              disabled={batchRegenerateMutation.isPending}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerate All QR Codes
            </Button>
            
            <Button
              onClick={() => queryClient.invalidateQueries(['qr-health', eventId])}
              variant="ghost"
            >
              <Search className="h-4 w-4 mr-2" />
              Re-scan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delegates List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Delegate QR Status
            </CardTitle>
            
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showOnlyIssues}
                  onChange={(e) => setShowOnlyIssues(e.target.checked)}
                  className="rounded"
                />
                Show only issues
              </label>
              
              <Input
                placeholder="Search delegates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-48"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredDelegates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {showOnlyIssues ? (
                  <>
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                    <p>All QR codes are healthy!</p>
                  </>
                ) : (
                  <p>No delegates found</p>
                )}
              </div>
            ) : (
              filteredDelegates.map((delegate) => (
                <div
                  key={delegate.id}
                  className={`border rounded-lg p-3 ${
                    delegate.qrStatus?.healthy 
                      ? 'border-green-200 bg-green-50/50' 
                      : 'border-red-200 bg-red-50/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {delegate.qrStatus?.healthy ? (
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                      )}
                      
                      <div>
                        <div className="font-medium">{delegate.name || 'Unnamed'}</div>
                        <div className="text-sm text-slate-500">{delegate.email}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {delegate.qrStatus?.format}
                      </Badge>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedDelegate(
                          expandedDelegate === delegate.id ? null : delegate.id
                        )}
                      >
                        {expandedDelegate === delegate.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      
                      {!delegate.qrStatus?.healthy && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => regenerateMutation.mutate(delegate.id)}
                          disabled={regenerateMutation.isPending}
                        >
                          <RefreshCw className={`h-4 w-4 ${
                            regenerateMutation.isPending ? 'animate-spin' : ''
                          }`} />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedDelegate === delegate.id && (
                    <div className="mt-3 pt-3 border-t text-sm space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-slate-500">Badge Code:</span>
                          <span className="ml-2 font-mono">{delegate.badge_code || '(none)'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Checked In:</span>
                          <span className="ml-2">{delegate.checked_in ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-slate-500">QR Data:</span>
                        <code className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded block mt-1 break-all">
                          {delegate.qr_data || '(empty)'}
                        </code>
                      </div>
                      
                      {delegate.qrStatus?.issues?.length > 0 && (
                        <div>
                          <span className="text-slate-500">Issues:</span>
                          <ul className="mt-1 space-y-1">
                            {delegate.qrStatus.issues.map((issue, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-red-600">
                                <AlertTriangle className="h-3 w-3" />
                                {issue.message}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          
          {filteredDelegates.length > 10 && (
            <div className="text-center text-sm text-slate-500 mt-4">
              Showing {filteredDelegates.length} delegates
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QRHealthCheck;
